import React, { useState, useEffect } from "react";
import { 
  Box, 
  Container, 
  Paper, 
  Typography, 
  Button, 
  Alert, 
  CircularProgress,
  TextField,
  Stack
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon } from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getQREventById, getClassroomById, findActiveClassroomBookingNow, getTimetableEntryById } from "../context/Api";
import { calculateDistance, getUserGeolocation } from "../utils/geolocationUtils";

const ExternalAttendance = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");

  useEffect(() => {
    const processQR = async () => {
      try {
        const qrData = searchParams.get("data");
        if (!qrData) {
          setError("Invalid QR code data");
          setLoading(false);
          return;
        }

        let parsed;
        try {
          parsed = JSON.parse(decodeURIComponent(qrData));
        } catch (e) {
          setError("Invalid QR code format");
          setLoading(false);
          return;
        }

        // Check timestamp to prevent proxy (QR codes expire after 5 minutes)
        if (parsed.ts) {
          const now = Date.now();
          const qrTime = parsed.ts;
          const timeDiff = now - qrTime;
          const fiveMinutes = 5 * 60 * 1000;
          
          if (timeDiff > fiveMinutes) {
            setError("QR code has expired. Please scan a fresh QR code.");
            setLoading(false);
            return;
          }
        }

        if (parsed.t === "classroom") {
          const classroom = await getClassroomById(parsed.id);
          if (!classroom) {
            setError("Classroom not found");
            setLoading(false);
            return;
          }

          const activeBooking = await findActiveClassroomBookingNow(classroom.tenantId, classroom.id);
          if (!activeBooking) {
            setError("No active booking for this classroom at this time");
            setLoading(false);
            return;
          }

          // Validate location
          const geo = await getUserGeolocation();
          const dist = calculateDistance(geo.latitude, geo.longitude, classroom.latitude, classroom.longitude);
          if (dist > (classroom.radius || 100)) {
            setError("You are not at the classroom location");
            setLoading(false);
            return;
          }

          setSuccess(true);
        } else if (parsed.t === "lecture") {
          const timetableEntry = await getTimetableEntryById(parsed.id);
          if (!timetableEntry || !timetableEntry.active) {
            setError("Lecture not found or inactive");
            setLoading(false);
            return;
          }

          // Get classroom for location validation
          const classroom = await getClassroomById(timetableEntry.classroomId);
          if (!classroom) {
            setError("Classroom not found");
            setLoading(false);
            return;
          }

          // Check if current time matches the lecture time
          const now = new Date();
          const currentWeekday = now.getDay();
          const [startH, startM] = timetableEntry.startTime.split(":").map(Number);
          const [endH, endM] = timetableEntry.endTime.split(":").map(Number);
          
          // Check weekday match
          if (currentWeekday !== timetableEntry.weekday) {
            const weekdayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
            setError(`This lecture is scheduled for ${weekdayNames[timetableEntry.weekday]}, not today`);
            setLoading(false);
            return;
          }

          // Check time window
          const currentMinutes = now.getHours() * 60 + now.getMinutes();
          const startMinutes = startH * 60 + startM;
          const endMinutes = endH * 60 + endM;
          
          if (currentMinutes < startMinutes - 15 || currentMinutes > endMinutes + 15) {
            setError(`Lecture time is ${timetableEntry.startTime} - ${timetableEntry.endTime}. Current time is outside the allowed window.`);
            setLoading(false);
            return;
          }

          // Check date range if specified
          if (timetableEntry.startDate || timetableEntry.endDate) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            if (timetableEntry.startDate) {
              const startDate = new Date(timetableEntry.startDate);
              startDate.setHours(0, 0, 0, 0);
              if (today < startDate) {
                setError("This lecture has not started yet");
                setLoading(false);
                return;
              }
            }
            
            if (timetableEntry.endDate) {
              const endDate = new Date(timetableEntry.endDate);
              endDate.setHours(0, 0, 0, 0);
              if (today > endDate) {
                setError("This lecture has ended");
                setLoading(false);
                return;
              }
            }
          }

          // Validate location
          const geo = await getUserGeolocation();
          const dist = calculateDistance(geo.latitude, geo.longitude, classroom.latitude, classroom.longitude);
          if (dist > (classroom.radius || 100)) {
            setError("You are not at the classroom location");
            setLoading(false);
            return;
          }

          setSuccess(true);
        } else if (parsed.t === "qr_event") {
          const event = await getQREventById(parsed.id);
          if (!event || !event.active) {
            setError("Event not found or inactive");
            setLoading(false);
            return;
          }

          // Validate time window
          const now = new Date();
          const today = event.date;
          const start = new Date(`${today}T${event.startTime}:00`);
          const end = new Date(`${today}T${event.endTime}:00`);
          
          if (end <= start) {
            end.setDate(end.getDate() + 1);
          }
          
          if (now < start || now > end) {
            setError(`Event not within active time window. Event runs from ${event.startTime} to ${event.endTime} on ${today}`);
            setLoading(false);
            return;
          }

          // Validate location
          const geo = await getUserGeolocation();
          const dist = calculateDistance(geo.latitude, geo.longitude, event.latitude, event.longitude);
          if (dist > (event.radius || 100)) {
            setError("You are not at the event location");
            setLoading(false);
            return;
          }

          setSuccess(true);
        } else {
          setError("Invalid QR code type");
          setLoading(false);
          return;
        }

        setLoading(false);
      } catch (e) {
        console.error("Error processing QR:", e);
        setError("Failed to process QR code: " + (e.message || "Unknown error"));
        setLoading(false);
      }
    };

    processQR();
  }, [searchParams]);

  const handleSubmit = async () => {
    if (!userName || !userEmail) {
      setError("Please provide your name and email");
      return;
    }

    // Here you would save the attendance record
    // For now, we'll just show success
    setSuccess(true);
    setError("");
    
    // Redirect after 3 seconds
    setTimeout(() => {
      navigate("/");
    }, 3000);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#f5f5f5" }}>
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Processing QR Code...</Typography>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f5f5", py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
          {error ? (
            <>
              <ErrorIcon sx={{ fontSize: 64, color: "error.main", mb: 2, display: "block", mx: "auto" }} />
              <Typography variant="h5" gutterBottom align="center" color="error">
                Attendance Marking Failed
              </Typography>
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
              <Button variant="contained" fullWidth sx={{ mt: 3 }} onClick={() => navigate("/")}>
                Go Back
              </Button>
            </>
          ) : success ? (
            <>
              <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2, display: "block", mx: "auto" }} />
              <Typography variant="h5" gutterBottom align="center" color="success.main">
                Attendance Marked Successfully!
              </Typography>
              <Typography variant="body1" align="center" sx={{ mt: 2, mb: 3 }}>
                Your attendance has been recorded.
              </Typography>
              <Button variant="contained" fullWidth onClick={() => navigate("/")}>
                Close
              </Button>
            </>
          ) : (
            <>
              <Typography variant="h5" gutterBottom align="center">
                Mark Your Attendance
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
                Please provide your details to mark attendance
              </Typography>
              <Stack spacing={2}>
                <TextField
                  label="Your Name"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  fullWidth
                  required
                />
                <TextField
                  label="Your Email"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  fullWidth
                  required
                />
                {error && (
                  <Alert severity="error">{error}</Alert>
                )}
                <Button variant="contained" fullWidth onClick={handleSubmit} size="large">
                  Mark Attendance
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default ExternalAttendance;

